(() => {
    const DEFAULT_OPTIONS = {
        disable_size: 992,
        offset_top: {
            default: 0,
            max_width: {}
        }
    };

    const getOptions = () => {
        const runtimeOptions = typeof window !== 'undefined' && window.affix_box_options
            ? window.affix_box_options
            : {};

        return {
            ...DEFAULT_OPTIONS,
            ...runtimeOptions,
            offset_top: {
                ...DEFAULT_OPTIONS.offset_top,
                ...(runtimeOptions.offset_top || {})
            }
        };
    };

    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

    const parseIntSafe = (value, fallback = 0) => {
        const parsed = Number.parseInt(value, 10);
        return Number.isNaN(parsed) ? fallback : parsed;
    };

    const resolveOffsetTop = (options, windowWidth) => {
        let offsetTop = parseIntSafe(options.offset_top?.default, 0);
        const maxWidthMap = options.offset_top?.max_width || {};

        Object
            .keys(maxWidthMap)
            .map((key) => parseIntSafe(key, 0))
            .filter((key) => key > 0)
            .sort((a, b) => a - b)
            .forEach((breakpoint) => {
                if (windowWidth < breakpoint) {
                    offsetTop = parseIntSafe(maxWidthMap[breakpoint], offsetTop);
                }
            });

        return offsetTop;
    };

    class AffixBoxInstance {
        constructor(holder, targetSelector, options) {
            this.holder = holder;
            this.targetSelector = targetSelector;
            this.options = options;
            this.target = null;
            this.lastScrollY = window.scrollY;
            this.currentFixedTop = 0;
            this.containerTop = 0;
            this.containerHeight = 0;
            this.elementHeight = 0;
            this.maxRelativeTop = 0;
            this.topOffset = 0;
            this.minFixedTop = 0;
            this.maxFixedTop = 0;
            this.targetWidth = 0;
            this.targetLeft = 0;
            this.cleanupFns = [];
            this.resizeObserver = null;
        }

        init() {
            this.target = this.holder.querySelector(`:scope > ${this.targetSelector}`);

            if (!this.target) {
                return;
            }

            this.holder.style.position = 'relative';

            const onScroll = this.handleScroll.bind(this);
            const onResize = this.handleResize.bind(this);
            const onContainerChange = this.handleResize.bind(this);

            window.addEventListener('scroll', onScroll, { passive: true });
            window.addEventListener('resize', onResize);
            document.addEventListener('CHANGE_CONTAINER_RESOLUTION', onContainerChange);

            this.cleanupFns.push(() => window.removeEventListener('scroll', onScroll));
            this.cleanupFns.push(() => window.removeEventListener('resize', onResize));
            this.cleanupFns.push(() => document.removeEventListener('CHANGE_CONTAINER_RESOLUTION', onContainerChange));

            if (typeof ResizeObserver !== 'undefined') {
                this.resizeObserver = new ResizeObserver(() => this.handleResize());
                this.resizeObserver.observe(this.holder);
                this.resizeObserver.observe(this.target);

                if (this.holder.parentElement) {
                    this.resizeObserver.observe(this.holder.parentElement);
                }
            }

            this.measure();
            this.handleScroll();
        }

        unload() {
            this.cleanupFns.forEach((fn) => fn());
            this.cleanupFns = [];

            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
                this.resizeObserver = null;
            }

            if (this.target) {
                this.target.style.position = '';
                this.target.style.top = '';
                this.target.style.bottom = '';
                this.target.style.left = '';
                this.target.style.width = '';
                this.target.style.height = '';
            }

            this.holder.style.position = '';
            this.holder.style.height = '';
        }

        handleResize() {
            this.measure();
            this.handleScroll();
        }

        measure() {
            this.topOffset = resolveOffsetTop(this.options, window.innerWidth);

            this.holder.style.height = '';

            // Reset tijdelijke fixed/absolute styles zodat metingen niet op oude inline width blijven hangen.
            this.target.style.position = 'relative';
            this.target.style.top = '0';
            this.target.style.bottom = 'auto';
            this.target.style.left = '0';
            this.target.style.width = '100%';
            this.target.style.height = 'auto';

            const holderRect = this.holder.getBoundingClientRect();
            const parentHeight = this.holder.parentElement
                ? this.holder.parentElement.offsetHeight
                : 0;

            this.containerTop = holderRect.top + window.scrollY;
            this.elementHeight = this.target.offsetHeight;
            this.containerHeight = Math.max(this.holder.offsetHeight, parentHeight, this.elementHeight);
            this.maxRelativeTop = Math.max(this.containerHeight - this.elementHeight, 0);

            this.targetWidth = holderRect.width;
            this.targetLeft = holderRect.left;

            this.minFixedTop = Math.min(this.topOffset, window.innerHeight - this.elementHeight);
            this.maxFixedTop = this.topOffset;

            this.currentFixedTop = clamp(this.currentFixedTop, this.minFixedTop, this.maxFixedTop);

            this.holder.style.height = `${this.containerHeight}px`;
        }

        handleScroll() {
            if (!this.target) {
                return;
            }

            const currentScrollY = window.scrollY;
            const delta = currentScrollY - this.lastScrollY;
            this.lastScrollY = currentScrollY;

            if (window.innerWidth < parseIntSafe(this.options.disable_size, DEFAULT_OPTIONS.disable_size)) {
                this.holder.style.height = '';
                this.setRelative();
                return;
            }

            if (this.elementHeight <= window.innerHeight - this.topOffset) {
                const stickyRelativeTop = currentScrollY + this.topOffset - this.containerTop;

                if (stickyRelativeTop <= 0) {
                    this.setAbsolute(0);
                    return;
                }

                if (stickyRelativeTop >= this.maxRelativeTop) {
                    this.setAbsolute(this.maxRelativeTop);
                    return;
                }

                this.setFixed(this.topOffset);
                return;
            }

            if (delta > 0) {
                this.currentFixedTop = Math.max(this.minFixedTop, this.currentFixedTop - delta);
            } else if (delta < 0) {
                this.currentFixedTop = Math.min(this.maxFixedTop, this.currentFixedTop - delta);
            }

            const desiredDocumentTop = currentScrollY + this.currentFixedTop;
            const desiredRelativeTop = desiredDocumentTop - this.containerTop;

            if (desiredRelativeTop <= 0) {
                this.currentFixedTop = this.maxFixedTop;
                this.setAbsolute(0);
                return;
            }

            if (desiredRelativeTop >= this.maxRelativeTop) {
                this.currentFixedTop = this.minFixedTop;
                this.setAbsolute(this.maxRelativeTop);
                return;
            }

            this.setFixed(this.currentFixedTop);
        }

        setFixed(top) {
            this.target.style.position = 'fixed';
            this.target.style.top = `${top}px`;
            this.target.style.bottom = 'auto';
            this.target.style.left = `${this.targetLeft}px`;
            this.target.style.width = `${this.targetWidth}px`;
            this.target.style.height = 'auto';
            this.target.classList.add('isActive');
        }

        setAbsolute(relativeTop) {
            this.target.style.position = 'absolute';
            this.target.style.top = `${relativeTop}px`;
            this.target.style.bottom = 'auto';
            this.target.style.left = '0';
            this.target.style.width = '100%';
            this.target.style.height = 'auto';
            this.target.classList.remove('isActive');
        }

        setRelative() {
            this.target.style.position = 'relative';
            this.target.style.top = '0';
            this.target.style.bottom = 'auto';
            this.target.style.left = '0';
            this.target.style.width = '100%';
            this.target.style.height = 'auto';
            this.target.classList.remove('isActive');
        }
    }

    let instances = [];

    const initializeAffixBoxes = () => {
        const options = getOptions();

        instances.forEach((instance) => instance.unload());
        instances = [];

        if (window.innerWidth < parseIntSafe(options.disable_size, DEFAULT_OPTIONS.disable_size)) {
            return;
        }

        document.querySelectorAll('.affix-box-holder').forEach((holder) => {
            const instance = new AffixBoxInstance(holder, '.affix-box', options);
            instance.init();
            instances.push(instance);
        });
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initializeAffixBoxes);
    } else {
        initializeAffixBoxes();
    }

    let resizeTimer = null;

    const reinitializeOnResize = () => {
        if (resizeTimer) {
            window.clearTimeout(resizeTimer);
        }

        resizeTimer = window.setTimeout(() => {
            initializeAffixBoxes();
        }, 120);
    };

    window.addEventListener('resize', reinitializeOnResize);
    window.addEventListener('orientationchange', reinitializeOnResize);

    document.addEventListener('AWWW_PAGE_RELOAD_FIRST', initializeAffixBoxes);
})();
